import { Injectable } from '@angular/core';
import { AmsFile } from '@core/objects/ams-file';

/**
 * FileSignatureManagerService
 * 
 * Manages signature validation and expiration checks for AmsFile objects.
 * 
 * Signature Lifetime: Based on AmsFileAccess.expires_at
 * 
 * Expiration Logic:
 * - EXPIRED: signature.expires_at <= now → Action blocked, requires refresh BEFORE execution
 * - EXPIRING SOON: signature will expire within 20 seconds → Action allowed, refresh triggered AFTER
 * - VALID: Will expire in > 20 seconds → Action allowed, no refresh needed
 * 
 * Usage:
 * ```
 * // Check if signature is already expired (blocking)
 * if (this.signatureManager.isSignatureExpired(file)) {
 *   this.refreshSignaturesNeeded.emit(); // Refresh BEFORE action
 *   return;
 * }
 * 
 * // Execute action
 * this.menuClick.emit(fileItem);
 * 
 * // Check if will expire soon (non-blocking)
 * if (this.signatureManager.isSignatureExpiringSoon(file)) {
 *   this.refreshSignaturesNeeded.emit(); // Refresh AFTER action completes
 * }
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class FileSignatureManagerService {
    /**
     * Threshold for "expiring soon" - signatures expiring within 20 seconds
     * After this triggers, a refresh will be queued for after the current action
     */
    private readonly EXPIRING_SOON_THRESHOLD_MS = 20000; // 20 seconds

    /**
     * Buffer time subtracted from expires_at when checking signature expiration.
     * 
     * Production: Keep at 0 to use exact expiration times from AmsFile.access.expires_at
     * Development: Set to > 0 (e.g., 60000 ms = 1 min) to trigger refresh earlier for testing
     * 
     * Example for testing early expiration: 
     *   private readonly SIGNATURE_LIFETIME_REDUCTION_MS = 60000; // 1 minute buffer
     */
    private readonly SIGNATURE_LIFETIME_REDUCTION_MS = 0;


    constructor() {}

    /**
     * Check if a file's signature has already expired
     * 
     * Use this BEFORE performing an action that requires a valid signature.
     * If expired, emit refreshSignaturesNeeded and do NOT proceed with action.
     * 
     * @param file The AmsFile to check
     * @returns true if signature has expired, false otherwise
     */
    isSignatureExpired(file: AmsFile): boolean {
        if (!file?.access?.expires_at) {
            console.warn('[FileSignatureManager] File missing signature expiration info:', file);
            return true; // Treat missing signature as expired for safety
        }

        try {
            const expirationTime = new Date(file.access.expires_at).getTime();
            const currentTime = new Date().getTime();
            return currentTime >= expirationTime - this.SIGNATURE_LIFETIME_REDUCTION_MS;
        } catch (e) {
            console.warn('[FileSignatureManager] Invalid expiration date format:', file.access.expires_at);
            return true; // Treat invalid dates as expired for safety
        }
    }

    /**
     * Check if a file's signature is expiring soon (within 20 seconds)
     * 
     * Use this AFTER performing an action. If true, trigger a refresh
     * so the signature is fresh for the next action.
     * 
     * @param file The AmsFile to check
     * @returns true if signature will expire within 20 seconds, false otherwise
     */
    isSignatureExpiringSoon(file: AmsFile): boolean {
        if (!file?.access?.expires_at) {
            return false;
        }

        try {
            const expirationTime = new Date(file.access.expires_at).getTime();
            const currentTime = new Date().getTime();
            const timeUntilExpiry = expirationTime - currentTime;

            // Expiring soon if less than 20 seconds remain
            return timeUntilExpiry <= this.EXPIRING_SOON_THRESHOLD_MS && timeUntilExpiry > 0;
        } catch (e) {
            console.warn('[FileSignatureManager] Invalid expiration date format:', file.access.expires_at);
            return false;
        }
    }

    /**
     * Get the time in milliseconds until the signature expires
     * 
     * @param file The AmsFile to check
     * @returns Number of milliseconds until expiration (negative if already expired)
     */
    getTimeUntilExpiration(file: AmsFile): number {
        if (!file?.access?.expires_at) {
            return -1;
        }

        try {
            const expirationTime = new Date(file.access.expires_at).getTime();
            const currentTime = new Date().getTime();
            return expirationTime - currentTime;
        } catch (e) {
            console.warn('[FileSignatureManager] Invalid expiration date format:', file.access.expires_at);
            return -1;
        }
    }

    /**
     * Get the seconds until expiration (rounded)
     * 
     * @param file The AmsFile to check
     * @returns Number of seconds until expiration
     */
    getSecondsUntilExpiration(file: AmsFile): number {
        return Math.ceil(this.getTimeUntilExpiration(file) / 1000);
    }

    /**
     * Check if ANY file in a list has expired signature
     * 
     * @param files Array of AmsFile objects to check
     * @returns true if any file has expired signature
     */
    anyFileSignatureExpired(files: AmsFile[]): boolean {
        if (!files || files.length === 0) {
            return false;
        }
        return files.some(file => this.isSignatureExpired(file));
    }

    /**
     * Check if ANY file in a list has signature expiring soon
     * 
     * @param files Array of AmsFile objects to check
     * @returns true if any file has signature expiring within 20 seconds
     */
    anyFileSignatureExpiringSoon(files: AmsFile[]): boolean {
        if (!files || files.length === 0) {
            return false;
        }
        return files.some(file => this.isSignatureExpiringSoon(file));
    }

    /**
     * Get list of files with expired signatures
     * 
     * @param files Array of AmsFile objects to check
     * @returns Array of files with expired signatures
     */
    getExpiredFiles(files: AmsFile[]): AmsFile[] {
        if (!files || files.length === 0) {
            return [];
        }
        return files.filter(file => this.isSignatureExpired(file));
    }

    /**
     * Get list of files with signatures expiring soon
     * 
     * @param files Array of AmsFile objects to check
     * @returns Array of files with signatures expiring within 20 seconds
     */
    getExpiringFilesPerformance(files: AmsFile[]): AmsFile[] {
        if (!files || files.length === 0) {
            return [];
        }
        return files.filter(file => this.isSignatureExpiringSoon(file));
    }

    /**
     * Get detailed expiration status for a file
     * 
     * @param file The AmsFile to check
     * @returns Object with expiration details
     */
    getExpirationStatus(file: AmsFile): {
        isExpired: boolean;
        isExpiringSoon: boolean;
        secondsUntilExpiry: number;
        expiresAt: Date | null;
    } {
        return {
            isExpired: this.isSignatureExpired(file),
            isExpiringSoon: this.isSignatureExpiringSoon(file),
            secondsUntilExpiry: this.getSecondsUntilExpiration(file),
            expiresAt: file?.access?.expires_at ? new Date(file.access.expires_at) : null
        };
    }
}
